import { PROSE_NOTE_MAX_LENGTH, PROSE_STATEMENT_MAX_LENGTH } from '@ni/shared';
import { PROSE_NOTE_OVER_LIMIT, PROSE_STATEMENT_OVER_LIMIT } from './copy';

export { PROSE_NOTE_MAX_LENGTH, PROSE_STATEMENT_MAX_LENGTH };

/**
 * **How long one prose note may be** (owner ruling, 2026-09-13: "add a
 * character limit for individual notes, just on the UI for now"; raised
 * 2026-09-14, `docs/notes/2026-09-14-split-giant-intake-note.md`).
 *
 * It was 2,000, and 2,000 was a stopgap with the incident's number in it. On
 * 2026-09-13 a 7,313-character profile pasted into the New Person box became
 * **one** extraction call, which ran past the transport's 180-second ceiling
 * three times over before the create gave up; 2,000 was chosen as a length a
 * single call comfortably survives, and the over-line told the office to split
 * the paste by hand.
 *
 * **The splitter is now behind the box**, so that line would refuse exactly the
 * paste the splitter exists for. A note over 1,500 characters is cut on the
 * server before it is stored — each piece its own row, its own reading leg, its
 * own retry — so length is no longer what a single reading has to survive. What
 * is left for this number to be is a **sanity ceiling**: 20,000 characters is
 * about ten pages, and past ten pages the thing being pasted is a document
 * rather than a note, which is the import's job and not this box's. Hence the
 * over-line's new words: paste this in parts, rather than split it by hand.
 *
 * **And it is no longer only the page's rule.** It used to be: nothing under
 * `shared/` and nothing on the wire refused a longer note, so the cap said what
 * the office was offered rather than what could be filed. That stopped being
 * tolerable when the splitter went in — a body's length is now a count of
 * *reading legs* — so the number moved to `@ni/shared`, where both `CreateInputBody`
 * and `CreateSubjectBody` enforce it, and this file re-exports it rather than
 * restating it. A second copy here is exactly how a page and a server come to
 * disagree about what they accept.
 */

/**
 * **How long an organization's own statement may be**, and it is still the
 * 2026-09-13 number (`docs/notes/2026-09-14-split-giant-intake-note.md`).
 *
 * The note cap above rose because the splitter went in behind the boxes that use
 * it. **Nothing is behind this one.** An organization's statement — the New
 * organization box and the lookup modal's "Add what you know" — is read *inside
 * the request that files it*, under `ORG_STATEMENT_RESTRICTION`: one claim type,
 * two anchor kinds, `mint: never`, and no reading leg anywhere. It is not split,
 * it has no piece to retry, and a twenty-thousand-character statement would walk
 * straight back into the 180-second timeout the whole of this work exists to
 * close.
 *
 * So the number that was chosen for "what one reading survives" stays on the box
 * that is still one reading, and its over-line asks for a shorter paragraph
 * rather than for a second paste — see {@link PROSE_STATEMENT_OVER_LIMIT}.
 *
 * **And it is on the wire too**, beside the note cap and for the same reason a
 * cap on a page alone is not a cap: `@ni/shared` owns the number, both statement
 * bodies enforce it, and this file re-exports it rather than restating it.
 */

/**
 * **A capped prose box: how much it takes, and what it says once it is full.**
 *
 * The two travel together deliberately. A cap and an over-line are one decision
 * — twenty thousand characters because a splitter is behind it, so paste it in
 * parts; two thousand because nothing is, so shorten it — and a component that
 * took the number and reached for the sentence on its own is one edit away from
 * telling the office to paste an organization's statement in parts.
 */
export interface ProseLimit {
  cap: number;
  overLine: string;
}

/** The boxes with the splitter behind them: New Person, and the record's add box. */
export const NOTE_LIMIT: ProseLimit = {
  cap: PROSE_NOTE_MAX_LENGTH,
  overLine: PROSE_NOTE_OVER_LIMIT,
};

/** The boxes that are read in their own request: an organization's statement. */
export const STATEMENT_LIMIT: ProseLimit = {
  cap: PROSE_STATEMENT_MAX_LENGTH,
  overLine: PROSE_STATEMENT_OVER_LIMIT,
};

/**
 * How far a prose field is past its cap, in the length that is actually
 * measured.
 *
 * Trimmed on purpose, and for two reasons that agree. The settings fields are
 * capped by `z.string().trim().max(…)` (`shared/src/org-settings.ts`), so the
 * server trims before it counts; and every note surface sends `value.trim()`,
 * so the trimmed string is the one that goes. A count over the raw string would
 * sit there red on a value that would have gone through untouched.
 *
 * Zero when there is room left — the caller reads it as "over", not as a signed
 * distance.
 */
export function overBy(value: string, cap: number): number {
  return Math.max(0, value.trim().length - cap);
}

/** The same distance, against the cap a person's note is held to. */
export function noteOverBy(value: string): number {
  return overBy(value, PROSE_NOTE_MAX_LENGTH);
}

/** And against an organization statement's, which is a different number. */
export function statementOverBy(value: string): number {
  return overBy(value, PROSE_STATEMENT_MAX_LENGTH);
}
